<script lang="ts">
  // Glass-to-glass latency reference. Show this page on a screen, point a camera
  // at it, and photograph it next to an operator device showing that camera's
  // return feed: the difference between the two clocks is the full chain
  // (camera + SDI/NDI + encode + network + decode + display).
  // The barcode is the same format the server burns in, so the operator client
  // can decode it automatically when the screen fills the camera frame.
  import { onDestroy } from 'svelte';
  import { burninGeometry, encodeBurnin } from '../../../shared/src/burnin';
  import { ClockSync } from '../lib/latency';

  const clock = new ClockSync();
  let canvas = $state<HTMLCanvasElement | null>(null);
  let text = $state('');
  let raf = 0;

  async function sync() {
    for (let i = 0; i < 5; i++) {
      const t0 = Date.now();
      const r = await fetch('/api/time').then((x) => x.json());
      clock.add(t0, r.server, Date.now());
    }
  }
  void sync();
  const resync = setInterval(sync, 30_000);

  function draw() {
    const c = canvas;
    if (c) {
      const w = (c.width = c.clientWidth * devicePixelRatio);
      const h = (c.height = c.clientHeight * devicePixelRatio);
      const ctx = c.getContext('2d')!;
      const now = clock.serverNow();
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, w, h);
      const d = new Date(now);
      text = `${d.toLocaleTimeString([], { hour12: false })}.${String(d.getMilliseconds()).padStart(3, '0')}`;
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${Math.round(w / 9)}px ui-monospace, monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, w / 2, h * 0.42);
      const g = burninGeometry(w, h);
      const bits = encodeBurnin(now);
      ctx.fillStyle = '#000';
      ctx.fillRect(g.block / 2, g.y0 - g.block / 2, (bits.length + 1) * g.block, 2 * g.block);
      ctx.fillStyle = '#fff';
      bits.forEach((b, i) => b && ctx.fillRect(g.x0 + i * g.block, g.y0, g.block, g.block));
    }
    raf = requestAnimationFrame(draw);
  }
  raf = requestAnimationFrame(draw);
  onDestroy(() => {
    cancelAnimationFrame(raf);
    clearInterval(resync);
  });
</script>

<canvas bind:this={canvas}></canvas>
<p class="note">Server-synchronised clock (offset {Math.round(clock.offsetMs)} ms, rtt {Math.round(clock.bestRtt)} ms). Film this screen with a camera to measure glass-to-glass latency.</p>

<style>
  canvas { position: fixed; inset: 0; width: 100vw; height: 100vh; }
  .note { position: fixed; top: 8px; left: 0; right: 0; text-align: center; color: #888; font-size: 0.85rem; margin: 0; }
</style>
